import * as React from "react";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useTheme } from "@emotion/react";
import { useLayout } from "../../../middlewares/LayoutContext";
import drawerDim from "../../../theme/drawerDimensions";
import {
  Backdrop,
  Badge,
  Box,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  Tooltip,
  alpha,
} from "@mui/material";
import ProfileMenu from "../../Profile/Menu";
import { onMessage } from "firebase/messaging";
import { messaging } from "../../../firebase";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  receive,
  reset,
  receiveAll,
  readOne,
} from "../../../Redux/notificationSlice";
import {
  profileGetSignedURLRoute,
  readOneNotificationRoute,
  sendNotificationRoute,
} from "../../../routes/apiRoutes";
import { useSpring, animated } from "react-spring";
import { useNavigate } from "react-router";
import {
  createEventRoute,
  createOfferRoute,
  createProjectRoute,
  createVendorRoute,
} from "../../../routes/reactRoutes";
import {
  Campaign,
  DoneAllRounded,
  ElectricBolt,
  NotificationsNoneRounded,
} from "@mui/icons-material";
import OpenNotificationHeader from "./OpenNotificationHeader";
import axiosInstance from "../../../axios/axiosInstance";
import apiService from "../../../services/apiService";
import { t } from "i18next";
import notificationsMock from "../../../mocks/notificationsmock.json";

function AppTopBar() {
  const { handleDrawerToggle } = useLayout();
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state) => state.authenticationValues.currentUser
  );
  const avatar = useSelector((state) => state.authenticationValues.avatar);
  const messages = useSelector((state) => state.notificationValues.messages);
  const [pageInfo, setPageInfo] = React.useState(1);
  const [msgcount, setMsgCount] = React.useState(0);
  const [scrolling, setScrolling] = React.useState(false);
  const count = useSelector((state) => state.notificationValues.unreadCount);
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();

  //Bell animation//////////////////
  const [, setIsAnimating] = React.useState(false);
  const animation = useSpring({
    from: { transform: "rotate(0deg)" },
    to: async (next) => {
      await next({ transform: "rotate(-20deg)" });
      await next({ transform: "rotate(20deg)" });
      await next({ transform: "rotate(0deg)" });
    },
    config: { duration: 500 },
    onRest: () => setIsAnimating(false),
  });

  React.useEffect(() => {
    if (count) setIsAnimating(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  ///////////////////////////////////

  //Listening to firebase push message
  if (messaging) {
    onMessage(messaging, (payload) => {
      try {
        const message = {
          body: payload.data.body,
          title: payload.data.title,
          notificationId: payload.data.notificationId,
          date: new Date().toISOString(),
          msgType: payload.data.msgType,
          ...(payload.data.type &&
            payload.data.id && {
              type: payload.data.type,
              id: payload.data.id,
            }),
        };
        dispatch(receive({ messages: [message] }));
      } catch {}
    });
  }

  //Get all messages from backend on first render
  React.useEffect(() => {
    receiveMessages("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    messages.length > 0 && receiveMessagesScroll(listType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo]);

  async function receiveMessages(selected) {
    setIsLoading(true);
    try {
      console.log("Receive Messages", selected);
      const data = await apiService.get(
        `${sendNotificationRoute}?take=10&pageNo=1&type=${selected}&receiverId=${currentUser.id}`
      );
      console.log("Data", data);
      const oldMessages = [];
      data.notifications.forEach((notification) => {
        const notificationKey = `${notification.notificationData?.title}-${notification.notificationData?.body}`;
        oldMessages.push({
          body: notification.notificationData?.body,
          title: notification.notificationData?.title,
          notificationId: notification.id,
          isRead: notification.notificationReceiver[0].isRead,
          date: notification.createdAt,
          msgType: notification.notificationData?.msgType,
          key: notificationKey,
          ...(notification.notificationData?.type &&
            notification.notificationData?.id && {
              type: notification.notificationData?.type,
              id: notification.notificationData?.id,
            }),
        });
      });
      setMsgCount(data?.totalCount);
      dispatch(
        receiveAll({
          messages: [...oldMessages],
          unreadCount: data?.unreadCount,
        })
      );
    } catch {
    } finally {
      setIsLoading(false);
    }
  }
  const handleListScroll = debounce((event) => {
    const bottomMargin =
      event.target.clientHeight -
      event.target.scrollHeight +
      event.target.scrollTop;
    if (bottomMargin > -20 && messages.length < msgcount) {
      setPageInfo(1 + Math.floor(messages.length / 10));
    }
  }, 300);

  async function receiveMessagesScroll(selected) {
    setScrolling(true);
    try {
      const data = await apiService.get(
        `${sendNotificationRoute}?take=10&pageNo=${pageInfo}&type=${selected}&receiverId=${currentUser.id}`
      );
        const oldMessages = [...messages];
        data.notifications?.forEach((notification) => {
          const notificationKey = `${notification.notificationData?.title}-${notification.notificationData?.body}`;

          oldMessages.push({
            body: notification.notificationData?.body,
            title: notification.notificationData?.title,
            notificationId: notification.id,
            isRead: notification.notificationReceiver[0].isRead,
            date: notification.createdAt,
            msgType: notification.notificationData?.msgType,
            key: notificationKey,
            ...(notification.notificationData?.type &&
              notification.notificationData?.id && {
                type: notification.notificationData?.type,
                id: notification.notificationData?.id,
              }),
          });
        });
        dispatch(receiveAll({ messages: [...oldMessages] }));
    } catch {
    } finally {
      setScrolling(false);
    }
  }

  //show messages and read all messages on bell icon click
  const handleNotifications = (event) => {
    setAnchorNotify(event.currentTarget);
    setOpenNotify(true);
  };

  const readOneMessage = async (id) => {
    try {
      setIsLoading(true);
      await apiService.get(
        `${readOneNotificationRoute}?receiverId=${currentUser.id}&id=${id}`
      );
      setIsLoading(false);
      dispatch(readOne({ notificationId: id }));
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getLatestPreviewLink = async (url) => {
    try {
      setScrolling(true);
      const data = await apiService.post(
        profileGetSignedURLRoute,
        { url: url }
      );
      setScrolling(false);
      return data?.url || null;
    } catch (error) {
      setScrolling(false);
      return null;
    }
  };

  const handleMessageClick = async (id, type, messageData, event) => {
    if (id && type === "printing" && !event.openMessage) {
      const newLink = await getLatestPreviewLink(messageData.id);
      downloadFile(newLink);
      if (!messageData.isRead) {
        readOneMessage(messageData.notificationId);
      }
    } else {
      if (!event.openMessage) {
        if (!messageData.isRead) {
          readOneMessage(messageData.notificationId);
        }
        const typeToRouteMap = {
          project: createProjectRoute,
          offer: createOfferRoute,
          event: createEventRoute,
          vendor: createVendorRoute,
        };
        if (
          id &&
          (type.startsWith("moderation") ||
            Object.keys(typeToRouteMap).includes(type))
        ) {
          if (Object.keys(typeToRouteMap).includes(type)) {
            handleCloseNotify();
            navigate(`${typeToRouteMap[type]}?${type}Id=${id}`);
          } else if (type.startsWith("moderation")) {
            const entityType = type.split("-").pop();
            const detailsRoute = typeToRouteMap[entityType];

            if (detailsRoute) {
              handleCloseNotify();
              navigate(`${detailsRoute}?${entityType}Id=${id}&&moderator=true`);
            }
          }
        } else {
          setOpenNotification(true);
          setOpenData(messageData);
        }
      }
      event.openMessage = false;
    }
  };

  const downloadFile = (url) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };
  const handleMessageClickOpen = (messageData, event) => {
    event.openMessage = true;
    if (!messageData.isRead) {
      readOneMessage(messageData.notificationId);
    }
  };

  function timeAgo(date) {
    try {
      const now = new Date();
      const diff = now - new Date(date);

      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (seconds < 60) {
        return t("justNow");
      } else if (minutes < 60) {
        return t("minutesAgo", { minutes });
      } else if (hours < 24) {
        return t("hoursAgo", { hours });
      } else {
        return t("daysAgo", { days });
      }
    } catch {
      return null;
    }
  }
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const [listType, setListType] = React.useState("all");
  const [openNotification, setOpenNotification] = React.useState(false);
  const [openData, setOpenData] = React.useState({});
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [anchorNotify, setAnchorNotify] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [openNotify, setOpenNotify] = React.useState(false);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  React.useEffect(() => {
    if (!open) handleClose();
  }, [open]);
  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const handleCloseNotify = () => {
    setAnchorNotify(null);
    setOpenNotify(false);
  };
  const handleListType = (selected) => {
    if (selected !== listType) {
      dispatch(reset());
      setPageInfo(1);
      setListType(selected);
      receiveMessages(selected);
    }
  };
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${drawerDim.width}px)` },
        ml: { md: `${drawerDim.width}px` },
        height: drawerDim.height,
        borderBottom: `2px solid ${theme.palette.componentBackgrounds.main}`,
        background: theme.palette.whiteblack.main,
      }}
    >
      <OpenNotificationHeader
        open={openNotification}
        setOpen={setOpenNotification}
        data={openData}
      />
      <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ display: { md: "none" }, mx: 2 }}
        >
          <MenuIcon color="primary" />
        </IconButton>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "right",
            width: "100%",
            mx: 2.5,
            flexDirection: isRtl ? "row-reverse" : "row",
          }}
        >
          <Tooltip title={t("viewnotifications")}>
            <Badge
              badgeContent={count}
              overlap="circular"
              color="error"
            >
              <IconButton
                sx={{
                  width: 40,
                  height: 40,
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 1,
                }}
                onClick={handleNotifications}
              >
                {count > 0 && (
                  <animated.div style={{ ...animation, fontSize: "0.8rem" }}>
                    <NotificationsActiveIcon color="primary" />
                  </animated.div>
                )}
                {count === 0 && <NotificationsActiveIcon color="primary" />}
              </IconButton>
            </Badge>
          </Tooltip>
          <img
            src={avatar ? avatar : "avatar.svg"}
            alt="avatar"
            width={"50px"}
            height={"50px"}
            style={{ borderRadius: "50%", marginLeft: isRtl ? 0 : 10, marginRight: isRtl ? 10 : 0 }}
          ></img>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                color: theme.palette.whiteblack.contrastText,
                pl: 1,
                fontWeight: 700,
                fontSize: "1rem",
              }}
              noWrap
              component="div"
            >
              {!isRtl
                ? `${t("hi")} ${currentUser.firstName}`
                : `${currentUser.firstName} ${t("hi")}`}
            </Typography>
          </Box>
          <IconButton
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <KeyboardArrowDownIcon color="primary"></KeyboardArrowDownIcon>
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ vertical: -20, horizontal: 0 }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <ProfileMenu setOpen={setOpen} />
          </Menu>
          <Menu
            id="notification-menu"
            anchorEl={anchorNotify}
            slotProps={{
              paper: { sx: { width: 500, height: 600, overflow: "hidden" } },
            }}
            open={openNotify}
            onClose={handleCloseNotify}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            MenuListProps={{
              "aria-labelledby": "basic-notify-button",
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "550px",
                }}
              >
                <CircularProgress color="primary" />
              </div>
            ) : (
              <div dir={theme.direction} position={"relative"} width={"100%"}>
                <Box
                  sx={{
                    display: "grid",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    background: theme.palette.whiteblack.prominent,
                    width: "inherit",
                    mt: -1,
                    gridTemplateColumns: "1fr 1fr 1fr",
                  }}
                >
                  <div
                    style={{ width: "100%" }}
                    onClick={() => {
                      handleListType("all");
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        textAlign: "center",
                        py: 1,
                        width: "100%",
                        color:
                          listType === "all"
                            ? theme.palette.primary.main
                            : theme.palette.cancelText.disabled,
                        borderBottom:
                          listType === "all"
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.componentBackgrounds.border}`,
                        fontWeight: listType === "all" ? 600 : 400,
                        cursor: "pointer",
                      }}
                    >
                      {t("all")}
                    </Typography>
                  </div>
                  <div
                    style={{ width: "100%" }}
                    onClick={() => {
                      handleListType("Announcement");
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        textAlign: "center",
                        width: "100%",
                        py: 1,
                        cursor: "pointer",
                        borderBottom:
                          listType === "Announcement"
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.componentBackgrounds.border}`,
                        color:
                          listType === "Announcement"
                            ? theme.palette.primary.main
                            : theme.palette.cancelText.disabled,
                        fontWeight: listType === "Announcement" ? 600 : 400,
                      }}
                    >
                      {t("announcements")}
                    </Typography>
                  </div>
                  <div
                    style={{ width: "100%" }}
                    onClick={() => {
                      handleListType("Info");
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        textAlign: "center",
                        width: "100%",
                        py: 1,
                        cursor: "pointer",
                        fontWeight: listType === "Info" ? 600 : 400,
                        borderBottom:
                          listType === "Info"
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.componentBackgrounds.border}`,
                        color:
                          listType === "Info"
                            ? theme.palette.primary.main
                            : theme.palette.cancelText.disabled,
                      }}
                    >
                      {t("actionitems")}
                    </Typography>
                  </div>
                </Box>
                {messages.length > 0 ? (
                  <ul
                    onScroll={handleListScroll}
                    style={{
                      padding: 0,
                      overflow: "auto",
                      height: 559,
                    }}
                  >
                    {messages.map((message, index) => {
                      return (
                        <div
                          onClick={(e) =>
                            handleMessageClick(
                              message.id,
                              message.type,
                              message,
                              e
                            )
                          }
                          style={{
                            cursor: "pointer",
                            backgroundColor: !message.isRead
                              ? alpha(theme.palette.primary.contrastText, 0.1)
                              : theme.palette.whiteblack.main,
                          }}
                          key={index}
                        >
                          <Box sx={{ py: 1, px: 1.5 }}>
                            <Box display={"flex"} gap={2} alignItems={"center"}>
                              {message.msgType === "Info" ? (
                                <ElectricBolt
                                  sx={{
                                    fontSize: "1.1rem",
                                    color: "#ffbf00",
                                  }}
                                />
                              ) : (
                                <Campaign
                                  sx={{
                                    fontSize: "1.1rem",
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  fontSize: "0.9rem",
                                  fontStyle: "italic",
                                  color: !message.isRead
                                    ? theme.palette.cancelText.inactiveFixed
                                    : theme.palette.cancelText.inactive,
                                }}
                              >
                                {message.msgType === "Info"
                                  ? t("actionrequired")
                                  : t("announcement")}
                              </Typography>
                              {!message.isRead && (
                                <Tooltip title={t("markasread")}>
                                  <IconButton
                                    onClick={(e) =>
                                      handleMessageClickOpen(message, e)
                                    }
                                    sx={{ p: 0, m: 0 }}
                                  >
                                    <DoneAllRounded
                                      sx={{
                                        fontSize: "1rem",
                                        color:
                                          message.msgType === "Info"
                                            ? "#ffbf00"
                                            : theme.palette.primary.main,
                                      }}
                                    ></DoneAllRounded>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <ListItemText
                              sx={{ ml: 4 }}
                              primary={message.title}
                              primaryTypographyProps={{
                                sx: {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: "0.9rem",
                                  fontWeight: 500,
                                },
                              }}
                              secondary={
                                <Typography
                                  sx={{
                                    fontSize: "0.875rem",
                                    display: "-webkit-box",
                                    WebkitLineClamp: "2",
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  variant="body2"
                                >
                                  {message.body}
                                </Typography>
                              }
                            ></ListItemText>
                            {message.date && (
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontStyle: "italic",  
                                  color: theme.palette.cancelText.inactive,
                                  ml: 4,
                                }}
                              >
                                {timeAgo(message.date)
                                  ? timeAgo(message.date)
                                  : ""}
                              </Typography>
                            )}
                          </Box>
                          <Divider style={{ width: "100%" }} />
                        </div>
                      );
                    })}
                    <Backdrop
                      sx={{
                        color: "#fff",
                        position: "absolute",
                        height: "100%",
                        overflow: "hidden",
                        background: "#00000025",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                      }}
                      open={scrolling}
                    >
                      <CircularProgress />
                    </Backdrop>
                  </ul>
                ) : (
                  <Box
                    sx={{
                      height: "550px",
                      width: "100%",
                      display: "flex",
                      mt: -1,
                      flexDirection: "column",
                      background: "transparent",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <NotificationsNoneRounded
                      sx={{ fontSize: "3rem" }}
                    ></NotificationsNoneRounded>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 600 }}>
                      {t("nonotifications")}
                    </Typography>
                    <Typography>{t("notifyinfo")}</Typography>
                  </Box>
                )}
              </div>
            )}
          </Menu>
        </Toolbar>
      </div>
    </AppBar>
  );
}
export default AppTopBar;
